import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { Send, X, User, ShieldAlert } from 'lucide-react';

export default function HumanCoachChat({ isOpen, onClose, token }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const loadMessages = async () => {
        try {
            const data = await api.fetch('/profile/messages', {}, token);
            setMessages(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load chat');
        }
    };

    useEffect(() => {
        if (isOpen) loadMessages();
    }, [isOpen]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        // Frontend check: Tel onbeantwoorde berichten van atleet (hoewel backend ook beveiligt)
        let unreadCount = 0;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].sender === 'coach') break;
            if (messages[i].sender === 'athlete' && !messages[i].is_read) unreadCount++;
        }
        
        if (unreadCount >= 5) {
            alert('Je hebt het maximale aantal (5) onbeantwoorde vragen bereikt. Geef de coach even tijd om te antwoorden!');
            return;
        }

        const newMsg = { id: Date.now(), sender: 'athlete', message: input.trim(), created_at: new Date() };
        setMessages(prev => [...prev, newMsg]);
        setInput('');
        setIsLoading(true);

        try {
            await api.fetch('/profile/messages', {
                method: 'POST',
                body: JSON.stringify({ message: newMsg.message })
            }, token);
            loadMessages(); // Ververs om echte DB ID te krijgen
        } catch (e) {
            alert(e.message || 'Kon bericht niet versturen');
            setMessages(prev => prev.filter(m => m.id !== newMsg.id)); // Haal weg bij falen
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 w-[90vw] sm:w-[400px] h-[600px] max-h-[85vh] bg-zinc-900/95 backdrop-blur-2xl border border-zinc-700/50 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-8">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 p-4 flex justify-between items-center shadow-md">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm border border-white/30"><User size={20} className="text-white"/></div>
                    <div>
                        <h3 className="text-white font-bold tracking-wide">Coach Chat</h3>
                        <p className="text-orange-100 text-xs opacity-90">Heb je een vraag? Stel hem hier!</p>
                    </div>
                </div>
                <button onClick={onClose} className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"><X size={20}/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-black/20">
                {messages.length === 0 && (
                    <div className="text-center text-gray-500 text-sm mt-10 italic">
                        Stuur een bericht naar je coach om de conversatie te starten.
                    </div>
                )}
                {messages.map((msg, idx) => {
                    const isCoach = msg.sender === 'coach';
                    return (
                        <div key={msg.id || idx} className={`flex ${isCoach ? 'justify-start' : 'justify-end'}`}>
                            <div className={`max-w-[80%] p-3.5 rounded-2xl ${isCoach ? 'bg-zinc-800/80 text-gray-100 rounded-tl-sm border border-zinc-700/60 backdrop-blur-sm' : 'bg-orange-900/60 text-orange-50 rounded-tr-sm border border-orange-700/50 shadow-sm backdrop-blur-sm'}`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                                <div className={`text-[10px] mt-1 text-right ${isCoach ? 'text-gray-400' : 'text-orange-200/50'}`}>
                                    {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="p-4 bg-zinc-900 border-t border-zinc-800">
                <div className="relative flex items-center">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Typ je bericht..."
                        disabled={isLoading}
                        className="w-full bg-black/50 border border-zinc-700 rounded-full pl-5 pr-14 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50"
                    />
                    <button type="submit" disabled={!input.trim() || isLoading} className="absolute right-2 p-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 text-white rounded-full transition-colors flex items-center justify-center">
                        <Send size={16} />
                    </button>
                </div>
            </form>
        </div>
    );
}
